import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/shadcn/table'
import { TableCard } from '@/components/common/table/TableCard'
import { PaginationFooter } from '@/components/common/table/PaginationFooter'
import { LoaderTable } from '@/components/common/loaders/LoaderTable'
import { useGetApiCompaniesCurrentAnalysisfiles } from '@/api/generated/company-management-user/company-management-user'
import { AnalysisFileType } from '@/api/model/analysisFileType'
import { OrderType } from '@/api/model/orderType'
import { SortableTableHead } from '@/components/common/SortableTableHead'
import { DownloadButton } from '@/components/common/DownloadButton'

const DEFAULT_PAGE_SIZE = 20

function formatDate(ts: string) {
  return new Date(ts.endsWith('Z') ? ts : ts + 'Z').toLocaleDateString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  })
}

interface AnalysisFilesTabProps {
  fileType: typeof AnalysisFileType[keyof typeof AnalysisFileType]
}

export function AnalysisFilesTab({ fileType }: AnalysisFilesTabProps) {
  const { t } = useTranslation(['billing', 'common'])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [sort, setSort] = useState<string | undefined>('creationTimestamp')
  const [order, setOrder] = useState<typeof OrderType[keyof typeof OrderType] | undefined>(OrderType.Descending)

  const handleSort = (field: string | undefined, ord: typeof OrderType[keyof typeof OrderType] | undefined) => {
    setSort(field)
    setOrder(ord)
    setPage(1)
  }

  const { data, isLoading, isError } = useGetApiCompaniesCurrentAnalysisfiles({
    Offset: (page - 1) * pageSize,
    Limit: pageSize,
    Sort: sort,
    Order: order,
    fileType,
  })
  const raw = (data as any)?.data ?? data
  const items = raw?.items ?? []
  const totalCount: number = raw?.totalCount ?? 0

  return (
    <TableCard>
      <div className="flex-1 overflow-auto scrollbar-custom relative">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-card shadow-sm [&_th]:bg-card">
            <TableRow>
              <SortableTableHead field="creationTimestamp" currentSort={sort} currentOrder={order} onSort={handleSort}>{t('analysisFilesTab.columns.createdAt')}</SortableTableHead>
              <SortableTableHead field="fileType" currentSort={sort} currentOrder={order} onSort={handleSort}>{t('analysisFilesTab.columns.fileType')}</SortableTableHead>
              <TableHead className="w-[80px]">{t('analysisFilesTab.columns.download')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <LoaderTable isLoading={isLoading} isError={isError} isEmpty={items.length === 0} colSpan={3} emptyMessage={t('analysisFilesTab.empty')}>
              {items.map((item: any, i: number) => (
              <TableRow key={i}>
                <TableCell>{item.creationTimestamp ? formatDate(item.creationTimestamp) : '—'}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {item.fileType ? t(`tabs.analysisFileType.${item.fileType}`, { defaultValue: item.fileType }) : '—'}
                </TableCell>
                <TableCell><DownloadButton url={item.downloadUrl} /></TableCell>
              </TableRow>
              ))}
            </LoaderTable>
          </TableBody>
        </Table>
      </div>
      <PaginationFooter
        offset={(page - 1) * pageSize}
        limit={pageSize}
        totalCount={totalCount}
        onOffsetChange={(offset) => setPage(Math.floor(offset / pageSize) + 1)}
        onPageSizeChange={(size) => { setPageSize(size); setPage(1) }}
      />
    </TableCard>
  )
}
